import { useEffect, useMemo, useState } from 'react'
import { Bell, Calendar, Clock, Send, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { useAuth } from '@/contexts/AuthContext'
import {
  useCancelScheduledReminder,
  useScheduleReminder,
  useScheduledReminders,
  useSendReminder,
} from '@/hooks/useInvoices'
import { formatCurrency, formatDate, cn } from '@/lib/utils'
import type { Invoice, ScheduledReminder } from '@/types/invoice'

type Mode = 'now' | 'scheduled'

interface InvoiceReminderModalProps {
  invoice: Invoice | null
  isOpen: boolean
  onClose: () => void
}

function defaultScheduledAt(): string {
  // Maintenant + 1 heure, formaté pour <input type="datetime-local">
  const d = new Date(Date.now() + 60 * 60 * 1000)
  d.setSeconds(0, 0)
  const tzOffset = d.getTimezoneOffset() * 60_000
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16)
}

export function InvoiceReminderModal({ invoice, isOpen, onClose }: InvoiceReminderModalProps) {
  const { user } = useAuth()
  const sendReminder = useSendReminder()
  const scheduleReminder = useScheduleReminder()
  const cancelReminder = useCancelScheduledReminder()
  const { data: reminders } = useScheduledReminders(isOpen ? invoice?.id : undefined)
  const pendingReminders = useMemo(
    () => (reminders || []).filter((r): r is ScheduledReminder => r.status === 'pending'),
    [reminders]
  )

  const [mode, setMode] = useState<Mode>('now')
  const [scheduledAt, setScheduledAt] = useState<string>(defaultScheduledAt)

  useEffect(() => {
    if (isOpen) {
      setMode('now')
      setScheduledAt(defaultScheduledAt())
    }
  }, [isOpen])

  const { isOverdue, daysOverdue } = useMemo(() => {
    if (!invoice?.date_echeance) return { isOverdue: false, daysOverdue: 0 }
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const echeance = new Date(invoice.date_echeance)
    echeance.setHours(0, 0, 0, 0)
    const overdue = echeance.getTime() < today.getTime()
    const days = Math.floor((today.getTime() - echeance.getTime()) / (1000 * 60 * 60 * 24))
    return { isOverdue: overdue, daysOverdue: days }
  }, [invoice?.date_echeance])

  const scheduledDate = mode === 'scheduled' ? new Date(scheduledAt) : null
  const scheduledIsValid = scheduledDate && scheduledDate.getTime() > Date.now()

  const handleSubmit = async () => {
    if (!invoice) return

    if (mode === 'now') {
      try {
        await sendReminder.mutateAsync({
          invoiceId: invoice.id,
          userId: invoice.utilisateur_id,
        })
        toast.success('Relance envoyée')
        onClose()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Erreur lors de l\'envoi de la relance')
      }
      return
    }

    if (!scheduledDate || !scheduledIsValid) {
      toast.error('La date de planification doit être dans le futur')
      return
    }
    if (!user?.id) {
      toast.error('Utilisateur non identifié')
      return
    }

    try {
      await scheduleReminder.mutateAsync({
        invoiceId: invoice.id,
        scheduledAt: scheduledDate,
        userId: user.id,
      })
      toast.success(`Relance planifiée pour le ${scheduledDate.toLocaleString('fr-FR')}`)
      onClose()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de la planification')
    }
  }

  const handleCancelReminder = async (id: string) => {
    try {
      await cancelReminder.mutateAsync(id)
      toast.success('Relance planifiée annulée')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur lors de l\'annulation')
    }
  }

  const isSubmitting = sendReminder.isPending || scheduleReminder.isPending
  const submitDisabled =
    !invoice?.client?.email ||
    isSubmitting ||
    (mode === 'scheduled' && !scheduledIsValid)

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'now' ? 'Envoyer une relance' : 'Planifier une relance'}
    >
      <div className="space-y-4">
        {invoice && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('now')}
                className={cn(
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                  mode === 'now'
                    ? 'border-mint-500 bg-mint-50 text-mint-700'
                    : 'border-beige-200 bg-white text-brown-600 hover:bg-beige-50'
                )}
              >
                <Send className="w-4 h-4" />
                Maintenant
              </button>
              <button
                type="button"
                onClick={() => setMode('scheduled')}
                className={cn(
                  'flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
                  mode === 'scheduled'
                    ? 'border-mint-500 bg-mint-50 text-mint-700'
                    : 'border-beige-200 bg-white text-brown-600 hover:bg-beige-50'
                )}
              >
                <Calendar className="w-4 h-4" />
                Planifier
              </button>
            </div>

            {pendingReminders.length > 0 && (
              <div className="bg-gold-50 border border-gold-200 rounded-lg p-3 space-y-2">
                <p className="text-sm font-medium text-brown-800 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-gold-600" />
                  {pendingReminders.length === 1
                    ? 'Une relance est déjà planifiée'
                    : `${pendingReminders.length} relances déjà planifiées`}
                </p>
                <ul className="space-y-1.5">
                  {pendingReminders.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-center justify-between gap-2 bg-white rounded px-2.5 py-1.5 text-sm"
                    >
                      <span className="text-brown-700">
                        {new Date(r.scheduled_at).toLocaleString('fr-FR', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCancelReminder(r.id)}
                        disabled={cancelReminder.isPending}
                        className="text-brown-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Annuler cette relance"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="text-brown-600">
              {mode === 'now' ? 'Envoyer un email de relance' : 'Planifier l\'envoi d\'une relance'} pour la facture{' '}
              <span className="font-mono font-medium text-brown-900">{invoice.numero}</span> ?
            </p>

            <div className="bg-beige-50 rounded-lg p-3 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-brown-500">Destinataire</span>
                <span className="font-medium text-brown-900 truncate">
                  {invoice.client?.email || '—'}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-brown-500">Montant TTC</span>
                <span className="font-medium text-brown-900">
                  {formatCurrency(invoice.total_ttc || 0)}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-brown-500">Échéance</span>
                <span className={cn('font-medium', isOverdue ? 'text-red-600' : 'text-brown-900')}>
                  {formatDate(invoice.date_echeance)}
                </span>
              </div>
            </div>

            {mode === 'scheduled' && (
              <div className="space-y-2">
                <label htmlFor="reminder-scheduled-at" className="text-sm font-medium text-brown-700">
                  Date et heure d'envoi
                </label>
                <input
                  id="reminder-scheduled-at"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  min={defaultScheduledAt().slice(0, 16)}
                  className="w-full px-3 py-2 rounded-lg border border-beige-200 bg-white text-brown-900 focus:outline-none focus:ring-2 focus:ring-mint-500"
                />
                {!scheduledIsValid && (
                  <p className="text-xs text-red-600">La date doit être dans le futur.</p>
                )}
                <p className="text-xs text-brown-500">
                  L'envoi sera traité à cette heure (précision ~1 minute).
                </p>
              </div>
            )}

            {mode === 'now' && (
              isOverdue ? (
                <div className="bg-red-50 border-l-4 border-red-500 rounded p-3 text-sm text-red-700">
                  Échéance dépassée depuis {daysOverdue} jour{daysOverdue > 1 ? 's' : ''}. L'email indiquera la facture comme en retard.
                </div>
              ) : (
                <div className="bg-mint-50 border-l-4 border-mint-500 rounded p-3 text-sm text-brown-700">
                  L'email rappellera l'échéance à venir.
                </div>
              )
            )}

            {!invoice.client?.email && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded p-3 text-sm text-red-700">
                Ce client n'a pas d'adresse email — l'envoi va échouer.
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 pt-2 justify-end">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={submitDisabled}>
            {mode === 'now' ? (
              <>
                <Bell className="w-4 h-4 mr-1" />
                Envoyer la relance
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-1" />
                Planifier la relance
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
